import React from "react";
import { InlineMath } from "react-katex";
import {
  makeOptions,
  pick,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const PERIOD_LABELS = Object.freeze({
  2: "dwuletnią",
  3: "trzyletnią",
  4: "czteroletnią",
});

const RANDOM_VARIANTS = Object.freeze([
  { rate: 4, years: 2 },
  { rate: 6, years: 2 },
  { rate: 6, years: 3 },
  { rate: 8, years: 2 },
  { rate: 8, years: 4 },
  { rate: 10, years: 2 },
]);

function decimalLatex(value) {
  return Number(value.toFixed(4)).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).replace(",", "{,}");
}

function factorLatex(rate) {
  return decimalLatex(1 + rate / 100);
}

function capitalFormula(multiplier, exponent = null, capital = "K_0") {
  return `${capital}\\cdot${multiplier}${exponent == null ? "" : `^${exponent}`}`;
}

export function createTask(item, variant) {
  const { rate, years } = item;
  const capital=item.capital??"K_0";
  const correct = capitalFormula(`(${factorLatex(rate)})`, years,capital);
  const splitRate = capitalFormula(`(${factorLatex(rate / years)})`, years,capital);
  const doubledPeriods = capitalFormula(`(${factorLatex(rate / 2)})`, 2 * years,capital);
  const simpleInterest = capitalFormula(decimalLatex(1 + (years * rate) / 100),null,capital);
  const fixedOptions = item.options || null;
  const { options, correctIndex } = makeOptions(
    correct,
    [splitRate, doubledPeriods, simpleInterest],
    fixedOptions
  );

  return {
    type: "MaturaCompoundInterestFormulaClosedTask",
    data: {
      kind: "compoundInterestFormula",
      rate,
      years,
      capital,
      instruction: "Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.",
      prompt: (
        <span>
          Klient wpłacił do banku na {PERIOD_LABELS[years]} lokatę kwotę w wysokości <InlineMath math={`${capital}\\text{ zł}`} />.
          Po każdym rocznym okresie oszczędzania bank dolicza odsetki w wysokości <InlineMath math={`${rate}\\%`} /> od kwoty bieżącego kapitału znajdującego się na lokacie — zgodnie z procentem składanym.
        </span>
      ),
      afterLatex: (
        <span>
          Po {years === 2 ? "dwóch" : years === 3 ? "trzech" : "czterech"} latach oszczędzania kwota na lokacie, bez uwzględniania podatków, jest równa:
        </span>
      ),
      options,
      correctIndex,
      explanationSteps: [
        {
          title: "Korzystamy ze wzoru na procent składany z karty wzorów. Znajduje się on w dziale „Ciągi”, ponieważ kapitał w kolejnych latach tworzy ciąg geometryczny.",
          latex: "K_n=K_0\\cdot\\left(1+\\frac{p}{100}\\right)^n",
        },
        {
          title: `Zapisujemy mnożnik odpowiadający wzrostowi kapitału o ${rate}%.`,
          latex: `1+\\frac{${rate}}{100}=${factorLatex(rate)}`,
        },
        {
          title: `Podstawiamy do wzoru p=${rate} oraz n=${years}. Odsetki są kapitalizowane raz w roku, więc stosujemy ten sam mnożnik ${years} razy.`,
          latex: `K_${years}=K_0\\cdot\\left(1+\\frac{${rate}}{100}\\right)^{${years}}=${correct}`,
        },
      ],
    },
    source: {
      variant,
      exam: "MATURA",
      session: variant === "original" ? item.session??"2024_CZE" : undefined,
      taskNumber: item.taskNumber??"4",
      basedOn: `MATURA_${item.session??"2024_CZE"}_${item.taskNumber??"4"}`,
    },
    tags: ["matura", item.session==="2022_GRU"?"cke-2022-grudzien":"cke-2024-czerwiec", "procent składany"],
  };
}

export function generateTask(options = {}) {
  if(options.variant==='original'&&options.session==='2022_GRU')return createTask({capital:40000,rate:7,years:2,session:'2022_GRU',taskNumber:'2',options:[['A','40000\\cdot(1{,}07)^2'],['B','40000\\cdot(1{,}7)^2'],['C','40000\\cdot1{,}14'],['D','40000\\cdot1{,}49']]},'original');
  const isOriginal = options.variant === "original" && options.session === "2024_CZE";
  if (isOriginal) {
    return createTask({
      rate: 6,
      years: 3,
      options: [
        ["A", "K_0\\cdot(1{,}06)^3"],
        ["B", "K_0\\cdot(1{,}02)^3"],
        ["C", "K_0\\cdot(1{,}03)^6"],
        ["D", "K_0\\cdot1{,}18"],
      ],
    }, "original");
  }
  return createTask(pick(RANDOM_VARIANTS), "random");
}

export {
  checkClosedAnswer as checkAnswer,
  renderClosedAnswer as renderAnswer,
  renderClosedExplanation as renderExplanation,
  renderClosedInput as renderInput,
  renderClosedTask as renderTask,
  renderClosedUserAnswer as renderUserAnswer,
} from "../../../../matura/MaturaMay2026ClosedTaskSupport";
